import { Box, CircularProgress, FormLabel, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CurrencyField } from '~/components/GenericForm/CurrencyField';
import { BonusProposalProps } from '~/data/proposals';
import { BonusProgramConfig } from '~/data/rewarding/types';
import {
  CommonEmployeeProposalFormHandle,
  CommonEmployeeProposalFormProps,
  ProposalDataFormButtons,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalConfigSelector } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelector';
import { ProposalConfigSelectorOptionDescriptionRow as OptionDescriptionRow } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelectorOptionDescriptionRow';
import { ProposalResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalResponsibleHrSelect';
import { useProposalFormImperativeHandle } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalFormImperativeHandle';
import { useEmployeeDataForProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeDataForProposal';
import { BonusProposalFormProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeBonusProposalsLayout/useBonusProposalsLayout';
import { convertDateTypeDatePickerProps } from '~/utils/helpers/date';

import { useSxStyles } from '../Common/useStyles';

import { useBonusProposalForm } from './useBonusProposalForm';

type PropsType = CommonEmployeeProposalFormProps & {
  proposal: BonusProposalProps;
  formProps: BonusProposalFormProps;
};

export const BonusProposalForm = React.forwardRef<
  CommonEmployeeProposalFormHandle,
  PropsType
>((props, ref) => {
  const { proposal, employeeId, isNewProposal, onProposalUpdate, formProps } =
    props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { currency } = useEmployeeDataForProposal({ proposal });
  const {
    form,
    responsibleHRSelector,
    bonusProgramConfigSelector,
    handleFormSubmit,
    handleFormReset,
    readOnlyMode,
    canSubmitForm,
    isLoading,
    fieldProps,
  } = useBonusProposalForm({
    proposal,
    employeeId,
    onProposalUpdate,
    formProps,
  });

  useProposalFormImperativeHandle({ ref, handleFormSubmit });

  const { control, formState } = form;

  if (isLoading.responsibleHR) {
    return <CircularProgress />;
  }

  return (
    <Box autoComplete="off" component="form" sx={sxStyles.form}>
      <Controller
        name={'bonusType'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <ProposalConfigSelector
              FormControlProps={{ fullWidth: true, error: !!error }}
              value={value}
              onConfigChange={onChange}
              {...bonusProgramConfigSelector}
              customOptionDescription={(
                option: BonusProgramConfig,
                currency,
              ) => {
                const {
                  fEmplBonusValue,
                  fIncentiveShortTarget,
                  fIncentiveShortInfo,
                } = option;

                return (
                  <>
                    {!!fEmplBonusValue && (
                      <OptionDescriptionRow>{`${t(
                        'text_bonus_value',
                      )}: ${fEmplBonusValue} ${currency}`}</OptionDescriptionRow>
                    )}
                    {!!fIncentiveShortTarget && (
                      <OptionDescriptionRow>{`${t(
                        'laTargetEarning',
                      )}: ${fIncentiveShortTarget} ${currency}`}</OptionDescriptionRow>
                    )}
                    {!!fIncentiveShortInfo && (
                      <OptionDescriptionRow gap={true}>
                        {fIncentiveShortInfo}
                      </OptionDescriptionRow>
                    )}
                  </>
                );
              }}
              id={'hoursType'}
              label={t('laBonusName')}
              required={true}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'bonusAmount'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <CurrencyField
              label={t('laAmount')}
              value={value}
              onChange={onChange}
              currency={currency}
              required
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'paymentPeriodInfo'}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laPayPeriod')}
              {...field}
              fullWidth={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <Box sx={sxStyles.fieldSet}>
        <FormLabel>{t('laPayPeriod')}</FormLabel>
        <Box sx={sxStyles.dateRange}>
          <Controller
            name={'paymentPeriodStartDate'}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <DatePicker
                  label={t('laStartDate')}
                  {...convertDateTypeDatePickerProps({
                    value,
                    onChange,
                    ...fieldProps.datePickers.startDate,
                  })}
                  slotProps={{
                    textField: {
                      error: !!error?.message,
                      helperText: error?.message,
                    },
                  }}
                  disabled={readOnlyMode}
                />
              );
            }}
          />
          <Box>-</Box>
          <Controller
            name={'paymentPeriodEndDate'}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <DatePicker
                  label={t('laEndDate')}
                  {...convertDateTypeDatePickerProps({
                    value,
                    onChange,
                    ...fieldProps.datePickers.endDate,
                  })}
                  slotProps={{
                    textField: {
                      error: !!error?.message,
                      helperText: error?.message,
                    },
                  }}
                  disabled={readOnlyMode}
                />
              );
            }}
          />
        </Box>
      </Box>
      <Controller
        name={'recurrence'}
        control={control}
        render={({
          field: { value, onChange, ...field },
          fieldState: { error },
        }) => {
          return (
            <TextField
              {...field}
              onChange={(e) =>
                e.target.value === ''
                  ? onChange(null)
                  : onChange(e.target.value)
              }
              label={t('laReccurance')}
              fullWidth={true}
              value={value || ''}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'paymentDate'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <DatePicker
              label={t('laBonusDate')}
              {...convertDateTypeDatePickerProps({
                value,
                onChange,
              })}
              disabled={readOnlyMode}
              slotProps={{
                textField: {
                  required: true,
                  error: !!error?.message,
                  helperText: error?.message,
                },
              }}
            />
          );
        }}
      />
      <Controller
        name={'info'}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laInfo')}
              {...field}
              fullWidth={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <ProposalResponsibleHRSelect
        form={form}
        control={control}
        selectorSettings={responsibleHRSelector}
        disabled={readOnlyMode}
      />
      <ProposalDataFormButtons
        isNewProposal={isNewProposal}
        onFormSubmitWithDecision={handleFormSubmit}
        onFormReset={handleFormReset}
        formState={formState}
        readOnlyMode={readOnlyMode}
        submitDecisionAllowed={canSubmitForm}
      />
    </Box>
  );
});
